import { useCallback, useEffect, useState } from 'react';

export type SoundType = 
  | 'pawn-capture' | 'knight-capture' | 'bishop-capture' | 'rook-capture' | 'queen-capture' | 'king-capture'
  | 'brilliant' | 'great' | 'good' | 'inaccuracy' | 'mistake' | 'blunder'
  | 'check' | 'checkmate' | 'victory-easy' | 'victory-medium' | 'victory-hard'
  | 'move' | 'promotion'
  | 'arcane-nova-strike' | 'arcane-shield' | 'arcane-phantom-step'
  | 'arcane-hex-freeze' | 'arcane-soul-swap' | 'arcane-time-warp';

interface AudioSettings {
  masterVolume: number;
  moveVolume: number;
  captureVolume: number;
  feedbackVolume: number;
  celebrationVolume: number;
  muted: boolean;
}

class AudioManager {
  private audioContext: AudioContext | null = null;
  private audioCache = new Map<SoundType, AudioBuffer>();
  private settings: AudioSettings = {
    masterVolume: 0.7,
    moveVolume: 0.8,
    captureVolume: 0.9,
    feedbackVolume: 0.8,
    celebrationVolume: 1.0,
    muted: false
  };
  private isInitialized = false;

  async initialize() {
    if (this.isInitialized) return;
    
    try {
      // Try to create AudioContext
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Load user preferences
      const savedSettings = localStorage.getItem('chess-audio-settings');
      if (savedSettings) {
        this.settings = { ...this.settings, ...JSON.parse(savedSettings) };
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.warn('AudioContext not supported, falling back to HTML5 Audio', error);
    }
  }

  updateSettings(newSettings: Partial<AudioSettings>) {
    this.settings = { ...this.settings, ...newSettings };
    localStorage.setItem('chess-audio-settings', JSON.stringify(this.settings));
  }

  private getVolumeForType(soundType: SoundType): number {
    if (this.settings.muted) return 0;
    
    const baseVolume = this.settings.masterVolume;
    
    if (['pawn-capture', 'knight-capture', 'bishop-capture', 'rook-capture', 'queen-capture', 'king-capture'].includes(soundType)) {
      return baseVolume * this.settings.captureVolume;
    }
    
    if (['brilliant', 'great', 'good', 'inaccuracy', 'mistake', 'blunder'].includes(soundType)) {
      return baseVolume * this.settings.feedbackVolume;
    }
    
    if (['victory-easy', 'victory-medium', 'victory-hard', 'checkmate'].includes(soundType)) {
      return baseVolume * this.settings.celebrationVolume;
    }
    
    return baseVolume * this.settings.moveVolume;
  }

  private generateTone(frequency: number, duration: number, type: OscillatorType = 'sine'): AudioBuffer {
    if (!this.audioContext) throw new Error('AudioContext not available');
    
    const sampleRate = this.audioContext.sampleRate;
    const length = sampleRate * duration;
    const buffer = this.audioContext.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);
    
    for (let i = 0; i < length; i++) {
      const time = i / sampleRate;
      let value = 0;
      
      switch (type) {
        case 'sine':
          value = Math.sin(2 * Math.PI * frequency * time);
          break;
        case 'square':
          value = Math.sin(2 * Math.PI * frequency * time) > 0 ? 1 : -1;
          break;
        case 'triangle':
          value = 2 * Math.abs(2 * ((frequency * time) % 1) - 1) - 1;
          break;
        case 'sawtooth':
          value = 2 * ((frequency * time) % 1) - 1;
          break;
      }
      
      // Apply envelope
      const envelope = Math.exp(-time * 3); // Exponential decay
      data[i] = value * envelope;
    }
    
    return buffer;
  }

  private generateComplexSound(config: { frequencies: number[], durations: number[], types?: OscillatorType[] }): AudioBuffer {
    if (!this.audioContext) throw new Error('AudioContext not available');
    
    const totalDuration = Math.max(...config.durations);
    const sampleRate = this.audioContext.sampleRate;
    const length = sampleRate * totalDuration;
    const buffer = this.audioContext.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);
    
    config.frequencies.forEach((freq, index) => {
      const duration = config.durations[index] || config.durations[0];
      const type = config.types?.[index] || 'sine';
      const startSample = Math.floor(index * 0.05 * sampleRate); // Slight delay between tones
      
      for (let i = 0; i < Math.min(duration * sampleRate, length - startSample); i++) {
        const time = i / sampleRate;
        let value = Math.sin(2 * Math.PI * freq * time);
        const envelope = Math.exp(-time * (type === 'sine' ? 2 : 4));
        
        if (startSample + i < length) {
          data[startSample + i] += value * envelope * 0.3; // Mix multiple tones
        }
      }
    });
    
    return buffer;
  }

  private async generateSounds() {
    if (!this.audioContext) return;
    
    try {
      // Piece capture sounds
      this.audioCache.set('pawn-capture', this.generateTone(220, 0.1, 'sine')); // Light tick
      this.audioCache.set('knight-capture', this.generateTone(180, 0.15, 'triangle')); // Thud with character
      this.audioCache.set('bishop-capture', this.generateTone(330, 0.12, 'sawtooth')); // Sharp slice
      this.audioCache.set('rook-capture', this.generateTone(120, 0.2, 'square')); // Deep crash
      this.audioCache.set('queen-capture', this.generateComplexSound({
        frequencies: [440, 554, 659], durations: [0.3, 0.3, 0.3], types: ['sine', 'sine', 'sine']
      })); // Majestic chime
      this.audioCache.set('king-capture', this.generateComplexSound({
        frequencies: [523, 659, 784, 1047], durations: [0.5, 0.5, 0.5, 0.5]
      })); // Victory bell
      
      // Move quality feedback
      this.audioCache.set('brilliant', this.generateComplexSound({
        frequencies: [523, 659, 784], durations: [0.2, 0.2, 0.2]
      })); // Ascending chimes
      this.audioCache.set('great', this.generateTone(659, 0.15, 'sine')); // Clear ding
      this.audioCache.set('good', this.generateTone(440, 0.1, 'sine')); // Soft click
      this.audioCache.set('inaccuracy', this.generateTone(330, 0.1, 'triangle')); // Neutral tap
      this.audioCache.set('mistake', this.generateTone(220, 0.15, 'square')); // Warning beep
      this.audioCache.set('blunder', this.generateTone(110, 0.3, 'sawtooth')); // Harsh buzzer
      
      // Game events
      this.audioCache.set('check', this.generateComplexSound({
        frequencies: [880, 660], durations: [0.1, 0.2]
      })); // Warning chime
      this.audioCache.set('checkmate', this.generateComplexSound({
        frequencies: [264, 330, 396, 528], durations: [0.4, 0.4, 0.4, 0.6]
      })); // Dramatic crescendo
      this.audioCache.set('move', this.generateTone(440, 0.05, 'sine')); // Simple move sound
      this.audioCache.set('promotion', this.generateComplexSound({
        frequencies: [523, 659, 784, 1047], durations: [0.2, 0.2, 0.2, 0.4]
      })); // Royal coronation
      
      // Victory fanfares
      this.audioCache.set('victory-easy', this.generateComplexSound({
        frequencies: [440, 554, 659], durations: [0.3, 0.3, 0.4]
      })); // Simple victory
      this.audioCache.set('victory-medium', this.generateComplexSound({
        frequencies: [264, 330, 396, 528, 660], durations: [0.2, 0.2, 0.2, 0.3, 0.4]
      })); // Triumphant horns
      this.audioCache.set('victory-hard', this.generateComplexSound({
        frequencies: [264, 297, 330, 352, 396, 440, 495, 528], durations: [0.15, 0.15, 0.15, 0.15, 0.2, 0.2, 0.3, 0.5]
      })); // Full orchestral celebration

      // ── Arcane Blitz superpower sounds ──────────────────────────────────────
      // Nova Strike 💥 — fast descending explosion (square wave burst)
      this.audioCache.set('arcane-nova-strike', this.generateComplexSound({
        frequencies: [880, 440, 220], durations: [0.1, 0.15, 0.3],
        types: ['square', 'square', 'square']
      }));
      // Arcane Shield 🛡️ — ascending protective chime
      this.audioCache.set('arcane-shield', this.generateComplexSound({
        frequencies: [523, 659, 784], durations: [0.3, 0.3, 0.5],
        types: ['sine', 'sine', 'sine']
      }));
      // Phantom Step 👻 — high ethereal tone fading out
      this.audioCache.set('arcane-phantom-step', this.generateTone(1047, 0.45, 'sine'));
      // Hex Freeze ❄️ — descending ice-crack triangle wave
      this.audioCache.set('arcane-hex-freeze', this.generateComplexSound({
        frequencies: [220, 200, 180], durations: [0.2, 0.2, 0.4],
        types: ['triangle', 'triangle', 'triangle']
      }));
      // Soul Swap 🔄 — swirling back-and-forth chime
      this.audioCache.set('arcane-soul-swap', this.generateComplexSound({
        frequencies: [440, 660, 440], durations: [0.2, 0.3, 0.2],
        types: ['sine', 'sine', 'sine']
      }));
      // Time Warp ⏪ — rewinding sawtooth buzz
      this.audioCache.set('arcane-time-warp', this.generateComplexSound({
        frequencies: [880, 660, 440, 330], durations: [0.15, 0.15, 0.2, 0.4],
        types: ['sawtooth', 'sawtooth', 'sawtooth', 'sawtooth']
      }));
      
    } catch (error) {
      console.warn('Failed to generate sounds:', error);
    }
  }

  async playSound(soundType: SoundType, options?: { pitch?: number, delay?: number }) {
    if (!this.isInitialized) await this.initialize();
    if (!this.audioContext || this.settings.muted) return;
    
    // Generate sounds if not cached
    if (this.audioCache.size === 0) {
      await this.generateSounds();
    }
    
    const buffer = this.audioCache.get(soundType);
    if (!buffer) return;
    
    try {
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      
      source.buffer = buffer;
      gainNode.gain.value = this.getVolumeForType(soundType);
      
      if (options?.pitch) {
        source.playbackRate.value = options.pitch;
      }
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      const startTime = this.audioContext.currentTime + (options?.delay || 0);
      source.start(startTime);
      
    } catch (error) {
      console.warn('Failed to play sound:', soundType, error);
    }
  }

  getSettings(): AudioSettings {
    return { ...this.settings };
  }

  async resume() {
    if (this.audioContext && this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }
}

// Singleton instance
const audioManager = new AudioManager();

export function useAudioManager() {
  const [isReady, setIsReady] = useState(false);
  const [settings, setSettings] = useState(audioManager.getSettings());

  useEffect(() => {
    audioManager.initialize().then(() => setIsReady(true));
  }, []);

  const playSound = useCallback((soundType: SoundType, options?: { pitch?: number, delay?: number }) => {
    audioManager.playSound(soundType, options);
  }, []);

  const updateSettings = useCallback((newSettings: Partial<AudioSettings>) => {
    audioManager.updateSettings(newSettings);
    setSettings(audioManager.getSettings());
  }, []);

  const resumeAudio = useCallback(() => {
    audioManager.resume();
  }, []);

  return {
    isReady,
    settings,
    playSound,
    updateSettings,
    resumeAudio
  };
}

export { audioManager };